import { useState } from 'react';
import { Button, Input, InputAdornment, Tooltip, IconButton, CircularProgress } from '@mui/material';
import { ILastMile } from '../index';
import { Line } from '../styles';
import UndoIcon from '@mui/icons-material/Undo';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DownloadingIcon from '@mui/icons-material/Downloading';
import { newMoneyWithNegative, newMoney } from 'utils/newMoney';
import dayjs from 'dayjs';
import { costManagementService } from 'services/costManagementService';
import { toast } from 'react-toastify';

interface FormTableLineProps {
  element: ILastMile;
  onEditField: (id: number, field: keyof ILastMile, value: any) => void;
  onRollback: (id: number) => void;
  isEdited: boolean;
}

export function FormTableLine({ element, onEditField, onRollback, isEdited }: FormTableLineProps) {
  const [fetchingDefault, setFetchingDefault] = useState(false);

  const handleChange = (field: keyof ILastMile, value: any) => {
    onEditField(element.id, field, value);
  };

  const handleFetchDefaultUnloadingCost = async () => {
    setFetchingDefault(true);
    try {
      const result = await costManagementService.getDefaultUnloadingCost(element.id);
      onEditField(element.id, 'unloadingCost', result.unloadingCost);
    } catch {
      toast.error('Nenhum valor de descarregamento padrão encontrado para este romaneio');
    } finally {
      setFetchingDefault(false);
    }
  };

  const deliveryCount = element?.waybill?.deliveryCount || 0;
  const total = element.leaderCommission + element.defaultCost + element.costAdjustment + (element.additionalDeliveryTotal || 0) + (element.unloadingCost || 0);

  const getAdditionalDeliveryTooltip = () => {
    if (!element.additionalDelivery) return '';
    
    return element.additionalDeliveryTotalCalculation;
  };

  return (
    <Line
      className="bodyLine"
      style={{
        background: isEdited ? '#E8F2D2' : undefined,
        transition: 'background 0.2s, border 0.2s',
      }}
    >
      <td style={{ textAlign: 'left', paddingLeft: '29px' }}>{`${element?.waybill?.driver?.name || 'Não informado'} ${element?.waybill?.driver?.surname || ''}`}</td>
      <td>{element?.waybill?.vehicle?.licensePlate || 'Não informado'}</td>
      <td>{dayjs(element?.waybill?.deliveryDate).format('DD/MM/YYYY') || 'Não informado'}</td>
      <td>{element?.waybill?.route || 'Não informado'}</td>
      <td>{element?.waybill?.transshipPoint?.name || element?.waybill?.vehicle?.transshipPoint?.name || 'Não informado'}</td>
      <td onClick={(e: any) => e.target?.children[0]?.focus()}>
        <div className="boxAmount">
          <Input
            type="text"
            value={newMoney.apply((element.leaderCommission ?? 0).toString())}
            onChange={e => handleChange('leaderCommission', newMoney.clean(e.target.value))}
            onFocus={e => e.target.select()}
            inputProps={{
              style: { textAlign: 'center' },
              inputMode: 'decimal',
              pattern: '^[0-9,]*',
            }}
            startAdornment={<InputAdornment position="start" sx={{ padding: 0.5 }}>R$</InputAdornment>}
            sx={{
              width: { xs: 100, sm: 120 },
              height: 48,
              background: '#F7F7F7',
              fontSize: { xs: '0.95rem', sm: '1rem' },
              fontFamily: 'inherit',
              '& input': {
                padding: 0,
                height: 48,
                boxSizing: 'border-box',
              },
            }}
          />
        </div>
      </td>
      <td>
        {element.additionalDeliveryTotal != null ? `R$ ${newMoney.apply(element.additionalDeliveryTotal.toString())}` : 'R$ 0,00'}
        {!!element.additionalDelivery && deliveryCount > 0 && (
          <Tooltip title={getAdditionalDeliveryTooltip()} arrow>
            <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
              <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
      </td>
      <td>{element?.defaultCost ? `R$ ${newMoney.apply(element.defaultCost.toString())}` : 'Não informado'}</td>
      <td onClick={(e: any) => e.target?.children[0]?.focus()}>
        <div className="boxAmount" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Input
            type="text"
            value={newMoneyWithNegative.apply(element.unloadingCost ?? 0)}
            onChange={e => handleChange('unloadingCost', newMoneyWithNegative.clean(e.target.value))}
            onFocus={e => e.target.select()}
            inputProps={{
              style: { textAlign: 'center' },
              inputMode: 'decimal',
              pattern: '^-?[0-9,]*',
            }}
            startAdornment={<InputAdornment position="start" sx={{ padding: 0.5 }}>R$</InputAdornment>}
            sx={{
              width: { xs: 90, sm: 105 },
              height: 48,
              background: '#F7F7F7',
              fontSize: { xs: '0.95rem', sm: '1rem' },
              fontFamily: 'inherit',
              '& input': {
                padding: 0,
                height: 48,
                boxSizing: 'border-box',
              },
            }}
          />
          <Tooltip title="Buscar valor padrão de descarregamento">
            <span>
              <IconButton
                size="small"
                onClick={handleFetchDefaultUnloadingCost}
                disabled={fetchingDefault}
                sx={{ padding: '4px', color: 'text.secondary', '&:hover': { color: '#89BD23' } }}
              >
                {fetchingDefault ? <CircularProgress size={16} /> : <DownloadingIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </td>
      <td onClick={(e: any) => e.target?.children[0]?.focus()}>
        <div className="boxAmount">
          <Input
            type="text"
            value={newMoneyWithNegative.apply(element.costAdjustment)}
            onChange={e => handleChange('costAdjustment', newMoneyWithNegative.clean(e.target.value))}
            onFocus={e => e.target.select()}
            inputProps={{
              style: { textAlign: 'center' },
              inputMode: 'decimal',
              pattern: '^-?[0-9,]*',
            }}
            startAdornment={<InputAdornment position="start" sx={{ padding: 0.5 }}>R$</InputAdornment>}
            sx={{
              width: { xs: 100, sm: 120 },
              height: 48,
              background: '#F7F7F7',
              fontSize: { xs: '0.95rem', sm: '1rem' },
              fontFamily: 'inherit',
              '& input': {
                padding: 0,
                height: 48,
                boxSizing: 'border-box',
              },
            }}
          />
        </div>
      </td>
      <td>R$ {newMoneyWithNegative.apply(total)}</td>
      <td style={{ textAlign: 'center' }}>
        {isEdited && (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title="Reverter alterações">
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => onRollback(element.id)}
                style={{ minWidth: 32, padding: 4, borderRadius: 20 }}
            >
              <UndoIcon fontSize="small" />
            </Button>
            </Tooltip>
          </span>
        )}
      </td>
    </Line>
  );
}
